import React from 'react';
import { ArrowLeft, ArrowRight, Home, LogOut } from 'lucide-react';
import { LanguageCode } from '../types';
import { translations } from '../i18n/translations';

interface NavigationHistoryBarProps {
  currentTab: string;
  canGoBack: boolean;
  canGoForward: boolean;
  onGoBack: () => void;
  onGoForward: () => void;
  onGoHome: () => void;
  currentLang: LanguageCode;
  onLogout?: () => void;
}

export const NavigationHistoryBar: React.FC<NavigationHistoryBarProps> = ({
  currentTab,
  canGoBack,
  canGoForward,
  onGoBack,
  onGoForward,
  onGoHome,
  currentLang,
  onLogout,
}) => {
  if (currentTab === 'home') return null;

  const t = translations[currentLang];

  const getTabTitle = (tab: string) => {
    switch (tab) {
      case 'about':
        return t.aboutUs || 'HAKKIMIZDA';
      case 'activities':
        return t.activities || 'FAALİYETLER';
      case 'news':
        return t.news || 'HABERLER';
      case 'events':
        return t.events || 'ETKİNLİKLER';
      case 'culture':
        return t.culture || 'KÜRT KÜLTÜRÜ';
      case 'membership':
        return t.membership || 'ÜYELİK';
      case 'donate':
        return t.donate || 'DESTEK OL';
      case 'gallery':
        return t.gallery || 'GALERİ';
      case 'documents':
        return t.documents || 'BELGELER';
      case 'contact':
        return t.contact || 'İLETİŞİM';
      case 'live':
        return t.liveStream || 'CANLI YAYIN';
      case 'member':
      case 'member-panel':
        return t.memberPanel || 'ÜYE PANELİ';
      case 'admin':
        return t.adminPanel || 'YÖNETİM PANELİ';
      default:
        return 'SAYFA';
    }
  };

  return (
    <div className="fixed top-[76px] sm:top-[86px] left-0 right-0 z-30 bg-gradient-to-r from-[#0B1426] via-[#132238] to-[#0B1426] border-b border-[#38BDF8]/30 shadow-xl px-3 py-2 transition-all backdrop-blur-md">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-2">
        {/* Left Action: Geri (Back) Button */}
        <button
          onClick={onGoBack}
          disabled={!canGoBack}
          className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all ${
            canGoBack
              ? 'bg-[#132238] border border-[#38BDF8]/50 text-[#7DD3FC] hover:bg-[#38BDF8] hover:text-[#0B1426] hover:border-[#A5F3FC] hover:shadow-[0_0_15px_rgba(56,189,248,0.7)] cursor-pointer active:scale-95'
              : 'bg-[#0B1426]/60 text-gray-600 cursor-not-allowed opacity-40 border border-transparent'
          }`}
          title={t.goBack || 'Geri'}
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>{t.goBack || 'Geri'}</span>
        </button>

        {/* Center: Home Circle Icon + Page Title Badge */}
        <div className="flex items-center gap-2 bg-[#0B1426]/90 border border-[#38BDF8]/40 px-3.5 py-1 rounded-full shadow-inner">
          <button
            onClick={onGoHome}
            className="w-6 h-6 rounded-full bg-[#38BDF8] text-[#0B1426] flex items-center justify-center hover:bg-[#A5F3FC] hover:scale-110 active:scale-95 transition-all font-bold shadow-[0_0_10px_rgba(56,189,248,0.6)] flex-shrink-0 cursor-pointer"
            title={t.home || 'Ana Sayfaya Dön'}
          >
            <Home className="w-3.5 h-3.5" />
          </button>

          <span className="text-xs sm:text-sm font-black text-[#7DD3FC] tracking-wider uppercase truncate max-w-[130px] sm:max-w-xs drop-shadow-[0_0_8px_rgba(56,189,248,0.5)]">
            {getTabTitle(currentTab)}
          </span>
        </div>

        {/* Right Action: Çıkış Yap (on Admin / Member) or İleri (Forward) Button */}
        {currentTab === 'admin' || currentTab === 'member' || currentTab === 'member-panel' ? (
          <button
            onClick={onLogout}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all bg-red-600/90 border border-red-400 text-white hover:bg-red-600 hover:shadow-[0_0_15px_rgba(239,68,68,0.7)] cursor-pointer active:scale-95 shrink-0"
            title="Çıkış Yap"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Çıkış Yap</span>
          </button>
        ) : (
          <button
            onClick={onGoForward}
            disabled={!canGoForward}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all ${
              canGoForward
                ? 'bg-[#132238] border border-[#38BDF8]/50 text-[#7DD3FC] hover:bg-[#38BDF8] hover:text-[#0B1426] hover:border-[#A5F3FC] hover:shadow-[0_0_15px_rgba(56,189,248,0.7)] cursor-pointer active:scale-95'
                : 'bg-[#0B1426]/60 text-gray-600 cursor-not-allowed opacity-40 border border-transparent'
            }`}
            title={t.goForward || 'İleri'}
          >
            <span>{t.goForward || 'İleri'}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};
